/**
 * Physics nodes — forces, velocity, raycasting.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

export const SetVelocity: NodeBehaviour = {
  id: 'physics.setVelocity',
  name: 'Set Velocity',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'ArrowRight',
  description: 'Sets the linear velocity of a rigidbody.',
  keywords: ['speed', 'move'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Velocity X', 'f32', 0),
    input('y', 'Velocity Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setVelocity', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setVelocity', ctx.getInput('entity'), ctx.getInput('x'), ctx.getInput('y'));
  },
};

export const GetVelocity: NodeBehaviour = {
  id: 'physics.getVelocity',
  name: 'Get Velocity',
  category: 'Physics',
  isPure: true,
  color: '#4ecdc4',
  icon: 'ArrowRight',
  description: 'Gets the linear velocity of a rigidbody.',
  keywords: ['speed'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('vel', 'Velocity', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getVelocity', [ctx.resolveInput('entity')], 'vec2');
  },
  execute(ctx) {
    const r = ctx.bridge.call('bridge:getVelocity', ctx.getInput('entity')) as { x: number; y: number } | null;
    ctx.setOutput('vel', r);
    ctx.setOutput('x', (r as { x: number } | null)?.x ?? 0);
    ctx.setOutput('y', (r as { y: number } | null)?.y ?? 0);
  },
};

export const AddForce: NodeBehaviour = {
  id: 'physics.addForce',
  name: 'Add Force',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Zap',
  description: 'Applies a force to a rigidbody.',
  keywords: ['push', 'impulse'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Force X', 'f32', 0),
    input('y', 'Force Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:addForce', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:addForce', ctx.getInput('entity'), ctx.getInput('x'), ctx.getInput('y'));
  },
};

export const AddImpulse: NodeBehaviour = {
  id: 'physics.addImpulse',
  name: 'Add Impulse',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Zap',
  description: 'Applies an instant impulse to a rigidbody.',
  keywords: ['force', 'instant', 'burst'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Impulse X', 'f32', 0),
    input('y', 'Impulse Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:addImpulse', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:addImpulse', ctx.getInput('entity'), ctx.getInput('x'), ctx.getInput('y'));
  },
};

export const Raycast2D: NodeBehaviour = {
  id: 'physics.raycast',
  name: 'Raycast 2D',
  category: 'Physics',
  isPure: true,
  color: '#4ecdc4',
  icon: 'Minus',
  description: 'Casts a ray and returns the first hit.',
  keywords: ['ray', 'line', 'trace'],
  inputs: [
    input('originX', 'Origin X', 'f32', 0),
    input('originY', 'Origin Y', 'f32', 0),
    input('dirX', 'Direction X', 'f32', 1),
    input('dirY', 'Direction Y', 'f32', 0),
    input('distance', 'Max Distance', 'f32', 1000),
  ],
  outputs: [
    output('hit', 'Did Hit', 'bool'),
    output('entity', 'Hit Entity', 'entity'),
    output('point', 'Hit Point', 'vec2'),
    output('normal', 'Hit Normal', 'vec2'),
    output('dist', 'Distance', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:raycast2d', [
      ctx.resolveInput('originX'), ctx.resolveInput('originY'),
      ctx.resolveInput('dirX'), ctx.resolveInput('dirY'),
      ctx.resolveInput('distance'),
    ], 'raycastResult');
  },
  execute(ctx) {
    const r = ctx.bridge.call('bridge:raycast2d', ctx.getInput('originX'), ctx.getInput('originY'), ctx.getInput('dirX'), ctx.getInput('dirY'), ctx.getInput('distance')) as { hit: boolean; entity: number; point: { x: number; y: number }; normal: { x: number; y: number }; dist: number } | null;
    ctx.setOutput('hit', r?.hit ?? false);
    ctx.setOutput('entity', r?.entity ?? 0);
    ctx.setOutput('point', r?.point ?? { x: 0, y: 0 });
    ctx.setOutput('normal', r?.normal ?? { x: 0, y: 0 });
    ctx.setOutput('dist', r?.dist ?? 0);
  },
};

// ============================================================================
// TORQUE / ANGULAR
// ============================================================================

export const SetLinearDamping: NodeBehaviour = {
  id: 'physics.setLinearDamping',
  name: 'Set Linear Damping',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Wind',
  description: 'Sets linear velocity damping (drag).',
  keywords: ['drag', 'friction', 'resistance'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('damping', 'Damping', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setLinearDamping', [ctx.resolveInput('entity'), ctx.resolveInput('damping')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setLinearDamping', ctx.getInput('entity'), ctx.getInput('damping'));
  },
};

export const SetAngularDamping: NodeBehaviour = {
  id: 'physics.setAngularDamping',
  name: 'Set Angular Damping',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Wind',
  description: 'Sets angular velocity damping.',
  keywords: ['drag', 'friction', 'resistance'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('damping', 'Damping', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setAngularDamping', [ctx.resolveInput('entity'), ctx.resolveInput('damping')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setAngularDamping', ctx.getInput('entity'), ctx.getInput('damping'));
  },
};

// ============================================================================
// MOVEMENT HELPERS
// ============================================================================

export const MoveTowards: NodeBehaviour = {
  id: 'physics.moveTowards',
  name: 'Move Towards',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Move',
  description: 'Sets velocity to move towards a target position.',
  keywords: ['seek', 'follow', 'approach'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('targetX', 'Target X', 'f32', 0),
    input('targetY', 'Target Y', 'f32', 0),
    input('speed', 'Speed', 'f32', 100),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:moveTowards', [ctx.resolveInput('entity'), ctx.resolveInput('targetX'), ctx.resolveInput('targetY'), ctx.resolveInput('speed')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:moveTowards', ctx.getInput('entity'), ctx.getInput('targetX'), ctx.getInput('targetY'), ctx.getInput('speed'));
  },
};

export const StopMovement: NodeBehaviour = {
  id: 'physics.stopMovement',
  name: 'Stop Movement',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Square',
  description: 'Sets both linear and angular velocity to zero.',
  keywords: ['halt', 'freeze', 'zero'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:stopMovement', [ctx.resolveInput('entity')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:stopMovement', ctx.getInput('entity'));
  },
};

export const PHYSICS_NODES: readonly NodeBehaviour[] = [
  // Core
  SetVelocity, GetVelocity, AddForce, AddImpulse, Raycast2D,
  // Damping
  SetLinearDamping, SetAngularDamping,
  // Movement helpers
  MoveTowards, StopMovement,
];
